import React, { useState, useEffect } from 'react';
import { groupBookingsService } from '../../../services/groupBookingsService';
import SecureGroupPaymentModal from './SecureGroupPaymentModal';
import travelieTheme from '../../../styles/travelieTheme';
import { useResponsive } from '../../../hooks/useResponsive';

// Phase 6: Migrated to TravelieTheme - June 16, 2026

const GroupDetails = ({ group, onClose, onUpdate }) => {
  const { isMobile } = useResponsive();

  // Helper function for button styling
  const getButtonStyle = (type, isActive = false) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      tab: {
        ...baseStyle,
        background: isActive ? travelieTheme.colors.primary.main : 'transparent',
        color: isActive ? '#FFFFFF' : travelieTheme.colors.text.secondary,
        borderBottom: isActive ? 'none' : `2px solid transparent`
      }
    };

    return styles[type] || styles.primary;
  };

  // Helper function for payment status badge
  const getPaymentStatusStyle = (status) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
      fontSize: travelieTheme.typography.fontSize.xs,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      display: 'inline-block',
      textTransform: 'capitalize'
    };

    const statusStyles = {
      completed: { ...baseStyle, background: '#DCFCE7', color: '#166534' },
      pending: { ...baseStyle, background: '#FEF3C7', color: '#92400E' },
      partial: { ...baseStyle, background: '#DBEAFE', color: '#1E40AF' },
      failed: { ...baseStyle, background: '#FEE2E2', color: '#991B1B' }
    };

    return statusStyles[status] || statusStyles.pending;
  };
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSecurePaymentModal, setShowSecurePaymentModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  useEffect(() => {
    fetchGroupDetails();
  }, [group.id]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      const data = await groupBookingsService.getById(group.id);
      setParticipants(data.participants || []);
    } catch (error) {
      console.error('Error fetching group details:', error);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPaymentDetails = (participant) => {
    setSelectedParticipant(participant);
    setShowSecurePaymentModal(true);
  };

  const handleCloseSecurePaymentModal = () => {
    setSelectedParticipant(null);
    setShowSecurePaymentModal(false);
  };



  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };



  // Handle data that might already be parsed objects or JSON strings
  const packages = typeof group.packages === 'string' ? JSON.parse(group.packages || '[]') : (group.packages || []);
  const paymentSchedule = typeof group.payment_schedule === 'string' ? JSON.parse(group.payment_schedule || '[]') : (group.payment_schedule || []);
  const coordinator = typeof group.coordinator === 'string' ? JSON.parse(group.coordinator || '{}') : (group.coordinator || {});

  return (
    <div>
      {/* Header */}
      <div style={{
        padding: travelieTheme.spacing.lg,
        borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h2 style={{
          fontSize: travelieTheme.typography.fontSize.lg,
          fontWeight: travelieTheme.typography.fontWeight.semibold,
          color: travelieTheme.colors.text.primary,
          margin: 0
        }}>{group.title}</h2>
        <button 
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: travelieTheme.colors.text.secondary,
            padding: 0,
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: `color ${travelieTheme.transitions.fast}`
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = travelieTheme.colors.text.primary}
          onMouseLeave={(e) => e.currentTarget.style.color = travelieTheme.colors.text.secondary}
        >×</button>
      </div>

      <div style={{ padding: travelieTheme.spacing.lg }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: travelieTheme.spacing.xs,
          borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
          marginBottom: travelieTheme.spacing.lg,
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={getButtonStyle('tab', activeTab === 'overview')}
            onMouseEnter={(e) => {
              if (activeTab !== 'overview') e.currentTarget.style.background = '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'overview') e.currentTarget.style.background = 'transparent';
            }}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('participants')}
            style={getButtonStyle('tab', activeTab === 'participants')}
            onMouseEnter={(e) => {
              if (activeTab !== 'participants') e.currentTarget.style.background = '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'participants') e.currentTarget.style.background = 'transparent';
            }}
          >
            Participants ({participants.length})
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            style={getButtonStyle('tab', activeTab === 'payments')}
            onMouseEnter={(e) => {
              if (activeTab !== 'payments') e.currentTarget.style.background = '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'payments') e.currentTarget.style.background = 'transparent';
            }}
          >
            Payments
          </button>
        </div>

        <div>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.lg }}>
            <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow, marginBottom: travelieTheme.spacing.lg }}>
              <h3>Trip Details</h3>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md }}>
                <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                  <label>Destination</label>
                  <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>{group.destination}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                  <label>Travel Dates</label>
                  <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>
                    {new Date(group.start_date).toLocaleDateString()} - {new Date(group.end_date).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                  <label>Component Type</label>
                  <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>{group.component_type}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                  <label>Max Participants</label>
                  <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>
                    {group.max_participants || 'Unlimited'}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                  <label>Current Participants</label>
                  <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>{participants.length}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                  <label>Total Revenue</label>
                  <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>${participants.reduce((sum, p) => sum + parseFloat(p.total_amount || 0), 0).toFixed(2)}</div>
                </div>
              </div>
            </div>

            <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow, marginBottom: travelieTheme.spacing.lg }}>
              <h3>Packages</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.md }}>
                {packages.map((pkg, index) => (
                  <div key={index} style={{ padding: travelieTheme.spacing.md, background: "#F9FAFB", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: travelieTheme.spacing.sm }}>
                      <div style={{ fontSize: travelieTheme.typography.fontSize.md, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>{pkg.name}</div>
                      <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.status.success }}>${parseFloat(pkg.price || 0).toFixed(2)} per person</div>
                    </div>
                    {pkg.description && <p style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, marginTop: travelieTheme.spacing.xs }}>{pkg.description}</p>}
                    {pkg.inclusions && pkg.inclusions.length > 0 && (
                      <div style={{ marginTop: travelieTheme.spacing.sm }}>
                        <h5>Includes:</h5>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: travelieTheme.spacing.xs, marginTop: travelieTheme.spacing.xs }}>
                          {pkg.inclusions.map((inclusion, i) => (
                            <span key={i} style={{ display: "inline-block", padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`, background: "#DBEAFE", color: "#1E40AF", borderRadius: travelieTheme.components.card.borderRadius, fontSize: travelieTheme.typography.fontSize.xs }}>{inclusion}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {coordinator.name && (
              <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow, marginBottom: travelieTheme.spacing.lg }}>
                <h3>Group Coordinator</h3>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                    <label>Name</label>
                    <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>{coordinator.name}</div>
                  </div>
                  {coordinator.email && (
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Email</label>
                      <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>{coordinator.email}</div>
                    </div>
                  )}
                  {coordinator.phone && (
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs }}>
                      <label>Phone</label>
                      <div style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>{coordinator.phone}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {group.cancellation_policy && (
              <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow, marginBottom: travelieTheme.spacing.lg }}>
                <h3>Cancellation Policy</h3>
                <p>{group.cancellation_policy}</p>
              </div>
            )}

            <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow, marginBottom: travelieTheme.spacing.lg }}>
              <h3>Public Booking URL</h3>
              <div style={{ display: "flex", gap: travelieTheme.spacing.sm }}>
                <input
                  type="text"
                  value={group.public_url}
                  readOnly
                  style={{ flex: 1, padding: travelieTheme.spacing.sm, fontSize: travelieTheme.typography.fontSize.sm, border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, background: "#F9FAFB" }}
                />
                <button
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(group.public_url);
                      alert('URL copied to clipboard!');
                    }
                  }}
                  style={getButtonStyle("primary")}
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Participants Tab */}
        {activeTab === 'participants' && (
          <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.lg }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: travelieTheme.spacing.md }}>
              <h3>Group Participants</h3>
              <div style={{ display: "flex", gap: travelieTheme.spacing.lg }}>
                <div style={{ display: "flex", gap: travelieTheme.spacing.xs, alignItems: "center" }}>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>Total:</span>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>{participants.length}</span>
                </div>
                <div style={{ display: "flex", gap: travelieTheme.spacing.xs, alignItems: "center" }}>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>Revenue:</span>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>${participants.reduce((sum, p) => sum + parseFloat(p.total_amount || 0), 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: travelieTheme.spacing.xl, color: travelieTheme.colors.text.secondary }}>Loading participants...</div>
            ) : participants.length === 0 ? (
              <div style={{ textAlign: "center", padding: travelieTheme.spacing.xl, color: travelieTheme.colors.text.secondary }}>
                <p>No participants yet</p>
                <p>Share the public booking URL to get participants to join this group booking.</p>
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Package</th>
                    <th>Amount</th>
                    <th>Payment Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map((participant) => {
                    const bookingDetails = participant.booking_details ? 
                      (typeof participant.booking_details === 'string' ? 
                        JSON.parse(participant.booking_details) : participant.booking_details) : {};
                    return (
                      <tr key={participant.id}>
                        <td>
                          <div style={{ fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>
                            {participant.first_name} {participant.last_name}
                            {bookingDetails.additional_guests && bookingDetails.additional_guests.length > 0 && (
                              <div style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary }}>
                                + {bookingDetails.additional_guests.length} guest(s)
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary }}>{participant.email}</td>
                        <td>{participant.phone}</td>
                        <td>{participant.package_selected}</td>
                        <td>${parseFloat(participant.total_amount || 0).toFixed(2)}</td>
                        <td>
                          <span style={getPaymentStatusStyle(participant.payment_status || 'pending')}>
                            {participant.payment_status || 'pending'}
                          </span>
                        </td>
                        <td>{new Date(participant.joined_at).toLocaleDateString()}</td>
                        <td>
                          <button
                            onClick={() => handleViewPaymentDetails(participant)}
                            style={getButtonStyle('secondary')}
                            title="View payment details"
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            View Payments
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}


          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.lg }}>
            <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
              <h3>Payment Schedule</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.md }}>
                {paymentSchedule.map((payment, index) => (
                  <div key={index} style={{ padding: travelieTheme.spacing.md, background: "#F9FAFB", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: travelieTheme.spacing.md }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.sm }}>
                      <h4>{payment.name}</h4>
                      <p>{payment.percentage}% of total</p>
                      {payment.due_date && <p>Due: {formatDate(payment.due_date)}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
              <h3>Payment Summary</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.md }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Total Revenue:</span>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.md, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.status.success }}>{formatCurrency(group.total_revenue)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Total Participants:</span>
                  <span>{participants.length}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Average per Participant:</span>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.md, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.status.success }}>
                    {formatCurrency(participants.length > 0 ? group.total_revenue / participants.length : 0)}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ padding: travelieTheme.spacing.lg, background: "#FFFFFF", border: `1px solid ${travelieTheme.colors.neutral.border}`, borderRadius: travelieTheme.components.card.borderRadius, boxShadow: travelieTheme.components.card.boxShadow }}>
              <h3>Payment Status by Participant</h3>
              <div style={{ display: "flex", gap: travelieTheme.spacing.lg, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: travelieTheme.spacing.xs }}>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>Paid:</span>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.md, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>
                    {participants.filter(p => p.payment_status === 'paid').length}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: travelieTheme.spacing.xs }}>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>Pending:</span>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.md, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>
                    {participants.filter(p => p.payment_status === 'pending').length}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: travelieTheme.spacing.xs }}>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>Partial:</span>
                  <span style={{ fontSize: travelieTheme.typography.fontSize.md, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>
                    {participants.filter(p => p.payment_status === 'partial').length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Secure Payment Details Modal */}
      {showSecurePaymentModal && selectedParticipant && (
        <SecureGroupPaymentModal
          participant={selectedParticipant}
          onClose={handleCloseSecurePaymentModal}
        />
      )}
    </div>
  );
};

export default GroupDetails;
